// eslint-disable-next-line no-use-before-define
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import useTheme from '@mui/system/useTheme';

interface Props {
  loadingText?: string | null;
  visible?: boolean;
  subtitleSequence?: string[];
  onTimeout?: () => void;
}

// Animated spinner with two overlaid circles.
// The background circle is a full track; the foreground rotates while its arc grows and shrinks between ~1% and ~50%.
type SvgSpinnerProps = {
  size?: number;
  thickness?: number;
};

export const LoadingIcon: React.FC<SvgSpinnerProps> = ({ size = 40, thickness = 5 }) => {
  const theme = useTheme();
  const [id] = useState(() => Math.random().toString(36).slice(2));
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const minDash = Math.max(2, 0.01 * circumference);
  const maxDash = 0.5 * circumference;
  const trackColor = theme.palette.grey[theme.palette.mode === 'light' ? 200 : 800];
  const arcColor = theme.palette.mode === 'light' ? theme.palette.secondary.main : theme.palette.secondary.light;

  const rotateKey = `spinner-rotate-${id}`;
  const dashKey = `spinner-dash-${id}`;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="progressbar" aria-label="loading">
        <defs>
          <style>{`
            @keyframes ${rotateKey} {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            @keyframes ${dashKey} {
              0% {
                stroke-dasharray: ${minDash}px, ${circumference}px;
                stroke-dashoffset: 0;
              }
              50% {
                stroke-dasharray: ${maxDash}px, ${circumference}px;
                stroke-dashoffset: -${0.25 * circumference}px;
              }
              100% {
                stroke-dasharray: ${minDash}px, ${circumference}px;
                stroke-dashoffset: -${circumference}px;
              }
            }
          `}</style>
        </defs>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          <g style={{ transformOrigin: '50% 50%', animation: `${rotateKey} 1400ms linear infinite` }}>
            <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={trackColor} strokeWidth={thickness} />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={arcColor}
              strokeWidth={thickness}
              strokeLinecap="round"
              style={{ animation: `${dashKey} 1400ms ease-in-out infinite` }}
            />
          </g>
        </g>
      </svg>
    </div>
  );
};

const LoadingSpinner = (props: Props) => {
  // General loading spinner component.

  const { loadingText = 'Loading page', visible = true, subtitleSequence = [], onTimeout } = props;
  const [currentSubtitle, setCurrentSubtitle] = useState<string | null>(
    subtitleSequence.length > 0 ? subtitleSequence[0] : '',
  );

  // Keep latest onTimeout without changing interval effect dependencies
  const onTimeoutRef = useRef<Props['onTimeout']>(onTimeout);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    if (subtitleSequence.length === 0) {
      setCurrentSubtitle('');
      return;
    }
    if (subtitleSequence.length === 1) {
      setCurrentSubtitle(subtitleSequence[0]);
      return;
    }
    // Walk through the sequence once, then stop on the last entry
    let idx = 0;
    setCurrentSubtitle(subtitleSequence[idx]);
    const id = setInterval(() => {
      idx += 1;
      if (idx >= subtitleSequence.length) {
        if (onTimeoutRef.current) {
          onTimeoutRef.current();
        }
        clearInterval(id);
        return;
      }
      setCurrentSubtitle(subtitleSequence[idx]);
    }, 15000);
    return () => {
      clearInterval(id);
    };
  }, [subtitleSequence]);

  return (
    <Box
      sx={{
        display: visible ? 'flex' : 'none',
        flexDirection: 'column',
        gap: 1,
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}>
      <LoadingIcon />

      <Typography variant="body1" textAlign="center" fontWeight={600}>
        {loadingText}
      </Typography>

      <Typography variant="subtitle2" textAlign="center">
        {currentSubtitle}
      </Typography>
    </Box>
  );
};

export default LoadingSpinner;
